import { debounce, scrollTo, updateColumnCount, reanimateImages } from "../utilities/utils"
import PriceRange from "./price-range"
import { reinitializeSwipers } from '../utilities/swiper-element'
import { enableLazyVideos } from '../base.js'

// manage colleciton banners display
const hideBanners = () => {
  const cardsNumber = document.querySelectorAll("#grid-product .card").length

  // large banners display
  if (cardsNumber < 6)
    document.querySelectorAll(".banner--large").forEach((el) => (el.style.display = "none"))

  // small banners display
  if (cardsNumber < 13)
    document.querySelectorAll(".banner--small").forEach((el) => (el.style.display = "none"))
}

class FacetFiltersForm extends HTMLElement {
  constructor() {
    super()
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this)
    this.handleMobileSort = this.handleMobileSort.bind(this)
    this.handleSortForm = this.handleSortForm.bind(this)
    this.handleFilterForm = this.handleFilterForm.bind(this)

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event)
    }, 500)

    // Listen for input events on the form
    this.querySelector("form").addEventListener("input", this.debouncedOnSubmit.bind(this))

    // Set up mobile sort handling
    this.setupMobileSort()
  }

  setupMobileSort() {
    const mobileSortSelect = document.querySelector('#SortByMobile')
    if (mobileSortSelect) {
      // Remove any existing listeners to prevent duplicates
      mobileSortSelect.removeEventListener('change', this.handleMobileSort)
      // Add the new listener
      mobileSortSelect.addEventListener('change', this.handleMobileSort)
    }
  }

  handleMobileSort(event) {
    const form = document.querySelector('#FacetSortForm')
    if (form) {
      const select = form.querySelector('select')
      if (select) {
        select.value = event.target.value
        this.debouncedOnSubmit(event)
      }
    }
  }

  handleSortForm(form) {
    // Get the current filter parameters
    const filterForm = document.querySelector('#FacetFiltersForm')
    let filterParams = ''
    if (filterForm) {
      const filterFormData = new FormData(filterForm)
      filterParams = new URLSearchParams(filterFormData).toString()
    }

    // Get the sort parameters
    const sortParams = this.createSearchParams(form)

    // Combine parameters, ensuring sort parameters come last
    return filterParams
      ? `${filterParams}&${sortParams}`
      : sortParams
  }

  handleFilterForm() {
    const forms = []
    const sortFilterForms = document.querySelectorAll("form.facet-form")
    const sortFormsIDs = [
      "FacetAvailabilityForm",
      "FacetSortForm",
      "FacetFiltersForm",
      "FacetSearchForm",
    ]

    sortFilterForms.forEach((form) => {
      if (sortFormsIDs.includes(form.id)) {
        const searchParams = this.createSearchParams(form)
        if (searchParams !== "") {
          forms.push(searchParams)
        }
      }
    })

    const formsParams = forms.join("&").split("&")
    return [...new Set(formsParams)].join("&")
  }

  onSubmitHandler(event) {
    event.preventDefault()
    const form = event.target.closest('form')
    if (!form) return

    // Handle sort forms separately
    if (form.id === 'FacetSortForm') {
      const searchParams = this.handleSortForm(form)
      this.onSubmitForm(searchParams, event)
      return
    }

    // Handle all other forms
    const searchParams = this.handleFilterForm()
    this.onSubmitForm(searchParams, event)
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, "text/html")

    // select filters form, availability form and sort form input container to update
    const formSelectors =
      "#FacetFiltersForm .js-filter, #FacetAvailabilityForm .js-filter, #FacetSortForm .js-filter"
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(formSelectors)
    const facetDetailsElementsFromDom = document.querySelectorAll(formSelectors)

    // Remove facets that are no longer returned from the server
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove()
      }
    })

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest(".js-filter") : undefined
      return jsFilter ? element.id === jsFilter.id : false
    }
    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter(
      (element) => !matchesId(element),
    )

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id)
      // Element already rendered in the DOM so just update the innerHTML
      if (currentElement) {
        document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } =
            facetsToRender[index - 1]
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender)
            return
          }
        }

        if (elementToRender.parentElement) {
          const parentJsFilter = document.querySelector(`#${elementToRender.parentElement.id} .js-filter`)
          if (parentJsFilter) {
            parentJsFilter.before(elementToRender)
          } else {
            // If no .js-filter found, append to parent element
            elementToRender.parentElement.appendChild(elementToRender)
          }
        }
      }
    })

    // Reattach mobile sort handler after filters are updated
    const facetFiltersForm = document.querySelector('facet-filters-form')
    if (facetFiltersForm) {
      facetFiltersForm.setupMobileSort()
    }

    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId)

    if (countsToRender) {
      const closestJSFilterID = event.target.closest(".js-filter").id

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, event.target.closest(".js-filter"))
      }
    }

    FacetFiltersForm.renderAdditionalElements(parsedHTML)
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state
        ? event.state.searchParams
        : FacetFiltersForm.searchParamsInitial
      if (searchParams === FacetFiltersForm.searchParamsPrev) return
      FacetFiltersForm.renderPage(searchParams, null, false)
    }
    window.addEventListener("popstate", onHistoryChange)
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams
    const sections = FacetFiltersForm.getSections()
    document.getElementById("grid-product").classList.add("loading")

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`
      const filterDataUrl = (element) => element.url === url

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event)
    })

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams)
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }]
        FacetFiltersForm.renderFilters(html, event)
        FacetFiltersForm.renderProductGridContainer(html)
        document.getElementById("grid-product").classList.remove("loading")

        const stickyEnginePadding = 24

        scrollTo(
          document.querySelector("div.grid-archive__outer-container"),
          document.getElementById("grid-archive-header").offsetHeight + stickyEnginePadding,
        )
      })
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html
    FacetFiltersForm.renderFilters(html, event)
    FacetFiltersForm.renderProductGridContainer(html)
    document.getElementById("grid-product").classList.remove("loading")
  }

  static renderProductGridContainer(html) {
    document.getElementById("grid-product").innerHTML = new DOMParser()
      .parseFromString(html, "text/html")
      .getElementById("grid-product").innerHTML
    // Re-animate images consistently
    const grid = document.getElementById("grid-product")
    reanimateImages(grid)
    hideBanners()
    document
      .getElementById("grid-product")
      .querySelectorAll(".icon")
      .forEach((icon) => {
        icon.classList.add("loaded")
      })
    updateColumnCount()
    enableLazyVideos()
    reinitializeSwipers()
  }

  // update applied filters header, active filters count, products count and icons
  static renderAdditionalElements(html) {
    const gridProduct = document.getElementById("grid-product")
    if (gridProduct?.dataset?.id) {
      const sectionId = gridProduct.dataset.id
      this.updateElementFromHTML(`#active-filters-count-${sectionId}`, html)
      this.updateElementFromHTML(`#collection-products-count-${sectionId}`, html)
    }

    // Update the applied filters header inside the drawer
    this.updateElementFromHTML(".facets__header", html)

    // Update the facets drawer footer (including "Show X results" button)
    this.updateElementFromHTML(".facets__footer", html)

    document.querySelectorAll("facet-filters-form .icon").forEach((icon) => {
      icon.classList.add("loaded")
    })
  }

  static updateElementFromHTML(selector, html) {
    const dynamicElement = html.querySelector(selector)
    const currentElement = document.querySelector(selector)
    if (!dynamicElement || !currentElement) return
    currentElement.innerHTML = dynamicElement.innerHTML
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector("summary")
    const sourceSummary = source.querySelector("summary")

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML
    }
  }

  static updateURLHash(searchParams) {
    history.pushState(
      { searchParams },
      "",
      `${window.location.pathname}${searchParams && "?".concat(searchParams)}`,
    )
  }

  static getSections() {
    return [
      {
        section: document.getElementById("grid-product").dataset.id,
      },
    ]
  }

  createSearchParams(form) {
    const formData = new FormData(form)
    return new URLSearchParams(formData).toString()
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event)
  }

  onActiveFilterClick(event) {
    event.preventDefault()
    const searchQuery = new URL(event.currentTarget.href).search
    const searchParams = searchQuery.substring(1) // remove the '?'
    FacetFiltersForm.renderPage(searchParams)
  }
}

FacetFiltersForm.filterData = []
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1)
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1)
FacetFiltersForm.setListeners()

if (!customElements.get("facet-filters-form")) {
  customElements.define("facet-filters-form", FacetFiltersForm)
}

class FacetRemove extends HTMLElement {
  constructor() {
    super()
    this.querySelector("a").addEventListener("click", (event) => {
      event.preventDefault()
      const form =
        this.closest("facet-filters-form#facets") ||
        document.querySelector("facet-filters-form#facets")
      form.onActiveFilterClick(event)
    })
  }
}

if (!customElements.get("facet-remove")) {
  customElements.define("facet-remove", FacetRemove)
}

class Filters extends HTMLElement {
  connectedCallback() {
    hideBanners()
    updateColumnCount()
  }

  syncSorts = () => {
    if (document.querySelectorAll("#SortByMobile").length == 0) return

    // Update sort_by query parameter on select change
    // Sync sot_by dropdowns (mobile and desktop)
    const sortByDropdowns = document.querySelectorAll("#SortBy, #SortByMobile")

    sortByDropdowns.forEach((sortByDropdown) => {
      sortByDropdown.addEventListener("change", function (e) {
        // Sync sort_by dropdown selection (desktop and mobile)
        sortByDropdowns.forEach((select) => {
          select.value = e.target.value
        })

        // trigger sort form input event
        const inputEvent = new Event("input")
        document.querySelector("#FacetSortForm").dispatchEvent(inputEvent)
      })
    })
  }
}

if (!customElements.get("filters-component")) {
  customElements.define("filters-component", Filters)
}
